import * as React from 'react'

import { PartialCategory, PartialSection } from '@doist/help-center-utils'
import {
    Container,
    Hairline,
    SelectField,
    SideBarCategory,
    Text,
    UserContext,
} from '@doist/marketist'

import { useTranslation } from 'next-i18next'

import { ArticleGrid } from '../../shared/ArticleGrid/ArticleGrid'
import { getImageForCategory } from '../../shared/utils/getImageForCategory'

import css from './MainSection.module.css'

const ArticleGridId = 'article-grid-id'

export type MainSectionProps = {
    categories: PartialCategory[]
    category: PartialCategory
    section: PartialSection
}

export function MainSection({ categories, category, section }: MainSectionProps) {
    const { t, i18n } = useTranslation('helpcenter')
    const { webpSupport } = React.useContext(UserContext)

    const [currentCategory, setCurrentCategory] = React.useState(category)
    const [currentSection, setCurrentSection] = React.useState(section)

    function onSelectCategory(selectedCategoryId: PartialCategory['id']) {
        const selectedCategory = categories.find((c) => c.id === selectedCategoryId)

        if (!selectedCategory || currentCategory.id === selectedCategory.id) {
            return
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setCurrentCategory(selectedCategory)
        const [newSection] = selectedCategory.sections
        setCurrentSection(newSection)
    }

    function onSelectSection(selectedSectionId: PartialSection['id']) {
        const selectedSection = currentCategory.sections.find((c) => c.id === selectedSectionId)

        if (!selectedSection || currentSection.id === selectedSection.id) {
            return
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setCurrentSection(selectedSection)
    }

    React.useEffect(() => {
        /** Updates the browser route to have an easier way to share a category or section. */
        const langPrefix = i18n.language !== 'en' ? `/${i18n.language}` : ''
        window.history.replaceState(
            'category' + currentCategory.id,
            currentCategory.name,
            langPrefix + currentSection.href,
        )
    }, [i18n.language, currentCategory, currentSection])

    return (
        <Container tag="section" className={css.mainSection} heroSection>
            <aside className={css.mobileDropdowns}>
                <SelectField
                    id="category-dropdown"
                    label={t('category')}
                    value={currentCategory.id}
                    onChange={(e) =>
                        onSelectCategory(Number(e.currentTarget.value) as PartialCategory['id'])
                    }
                >
                    {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>
                    ))}
                </SelectField>

                <SelectField
                    id="section-dropdown"
                    label={t('section')}
                    value={currentSection.id}
                    onChange={(e) =>
                        onSelectSection(Number(e.currentTarget.value) as PartialSection['id'])
                    }
                >
                    {currentCategory.sections.map((section) => (
                        <option key={section.id} value={section.id}>
                            {section.name}
                        </option>
                    ))}
                </SelectField>
            </aside>

            <aside className={css.sideBar}>
                <ul>
                    {categories.map((cat) => (
                        <SideBarCategory
                            key={cat.id}
                            name={cat.name}
                            expanded={cat.id === currentCategory.id}
                            image={getImageForCategory(cat, webpSupport)}
                            links={cat.sections.map((s) => {
                                if (s.id === 0) {
                                    return t('allIntegrations')
                                }
                                return s.name
                            })}
                            activeLinkIndex={
                                cat.id === currentCategory.id
                                    ? cat.sections.findIndex((s) => s.id === currentSection.id)
                                    : undefined
                            }
                            onChange={(index: number) => onSelectSection(cat.sections[index].id)}
                            onCategoryClick={() => onSelectCategory(cat.id)}
                        />
                    ))}
                </ul>
            </aside>

            <section className={css.content}>
                <div className={css.heading}>
                    <Text tag="h2" size="major-base">
                        {currentCategory.name}
                    </Text>
                    <Text size="body-lg">
                        {currentSection.id === 0 ? t('allIntegrations') : currentSection.name}
                    </Text>
                </div>

                <Hairline />

                <ArticleGrid
                    id={ArticleGridId}
                    /** Only show the searchbar on the `All` section of the Integrations category */
                    title={
                        currentSection.id === 0 ? t('searchAllIntegations') : currentSection.name
                    }
                    articles={currentSection.articles}
                />
            </section>
        </Container>
    )
}
